import { Star } from "lucide-react";
import type { ReviewSummary } from "@/lib/api";
import { getCategoryMeta, getTagLabel } from "@/lib/review-meta";
import { useAppTranslation } from "@/i18n/useAppTranslation";

/**
 * Airbnb-style review summary block — overall average + 5-bar histogram +
 * per-category icon grid + top tag chips.
 *
 * The component is server-driven: the API returns `null` when there are
 * fewer than 3 eligible new-format reviews, so callers can place this
 * unconditionally and trust that legacy-only datasets stay collapsed.
 */
export function ReviewSummaryBlock({ summary }: { summary: ReviewSummary | null }) {
  const { t } = useAppTranslation();
  if (!summary) return null;
  const total = (Object.values(summary.ratingDistribution) as number[]).reduce((a, b) => a + b, 0);
  const overallAvg = total > 0
    ? (Object.entries(summary.ratingDistribution) as Array<[string, number]>)
        .reduce((sum, [stars, n]) => sum + Number(stars) * n, 0) / total
    : 0;

  return (
    <div className="mt-6 space-y-6 rounded-2xl border border-border p-5">
      <div className="grid gap-6 sm:grid-cols-[140px_1fr]">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-1">
            <Star className="h-5 w-5 fill-foreground text-foreground" />
            <span className="text-3xl font-semibold">{overallAvg.toFixed(2)}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {total} review{total === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          {([5, 4, 3, 2, 1] as const).map((stars) => {
            const n = summary.ratingDistribution[String(stars) as "1" | "2" | "3" | "4" | "5"] || 0;
            const pct = total > 0 ? (n / total) * 100 : 0;
            return (
              <div key={stars} className="flex items-center gap-2">
                <span className="w-3 text-right text-xs text-muted-foreground">{stars}</span>
                <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-foreground" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Object.entries(summary.categoryAverages).map(([slug, { avg }]) => {
          const meta = getCategoryMeta(slug);
          if (!meta) return null;
          const Icon = meta.icon;
          return (
            <div
              key={slug}
              className="flex flex-col items-center gap-1 rounded-xl border border-border p-3 text-center"
            >
              <Icon className="h-5 w-5 text-foreground" />
              <p className="text-xs text-muted-foreground">{meta.label}</p>
              <p className="text-sm font-semibold">{avg.toFixed(1)}</p>
            </div>
          );
        })}
      </div>

      {summary.topTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {summary.topTags.map((t) => (
            <span
              key={t.tag}
              className="inline-flex items-center rounded-full border border-border bg-muted/30 px-3 py-1 text-xs text-foreground"
            >
              {getTagLabel(t.tag)} · {t.count}
            </span>
          ))}
        </div>
      )}

      {/* Airbnb-style "How reviews work" affordance — links to the docs
          surface that explains the new category-and-tags system. Kept
          inline so it travels with the summary block on every page. */}
      <a
        href="/help/topic/trust-and-safety"
        className="inline-flex items-center text-xs font-medium text-foreground underline underline-offset-4 hover:opacity-80"
      >
        {t("reviews.how_reviews_work")}
      </a>
    </div>
  );
}
